import type { BusyButtonProps } from '@ztechapps/webui.button'
import { BusyButton } from '@ztechapps/webui.button'
import type { FunctionComponent } from 'react'
import React, { useState, useRef, useCallback } from 'react'
import { DashboardModal } from '@uppy/react'
import type { File } from '@ztechapps/common.contract'

import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'
import '@uppy/webcam/dist/style.min.css'
import '@uppy/screen-capture/dist/style.min.css'
import { useUppyFileUpload, type UppyRestrictions } from './useUppyFileUpload'

export type { UppyRestrictions } from './useUppyFileUpload'
export type OnSetFileMetaFunction = (file: File) => Record<string, any>
export type OnUploadCompleteFunction = (files: File[]) => Promise<void>

/**
 * Upload mode for FileUploader.
 * - 'modal': Opens Uppy DashboardModal with webcam, screen capture, drag-drop (default)
 * - 'simple': Direct file picker via native input, no modal
 */
export type FileUploaderMode = 'modal' | 'simple'

export type FileUploaderProps = {
  onUploadComplete?: OnUploadCompleteFunction
  onSetFileMeta?: OnSetFileMetaFunction
  disabled?: boolean
  uploadText?: string
  trigger?: FunctionComponent<BusyButtonProps>
  /** Optional restrictions for file uploads (size, type, count) */
  restrictions?: UppyRestrictions
  /**
   * Upload mode.
   * - 'modal': Opens Uppy DashboardModal (default)
   * - 'simple': Direct file picker, no modal
   */
  mode?: FileUploaderMode
  /**
   * Accepted file types for simple mode (e.g., '.pdf,.png,image/*').
   * Only used when mode='simple'.
   */
  accept?: string
  /** Callback when an error occurs during upload */
  onError?: (error: Error) => void
}

export const FileUploader = ({
  onUploadComplete,
  onSetFileMeta,
  disabled,
  uploadText = 'Upload files...',
  trigger,
  restrictions,
  mode = 'modal',
  accept,
  onError,
}: FileUploaderProps) => {
  const { uppy } = useUppyFileUpload({ onSetFileMeta, onUploadComplete, restrictions })
  const Trigger = trigger ?? BusyButton

  // Modal mode state
  const [modalOpen, setModalOpen] = useState(false)

  // Simple mode state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const onRequestClose = () => {
    setModalOpen(false)
  }

  const handleTriggerClick = useCallback(() => {
    if (mode === 'simple') {
      fileInputRef.current?.click()
    } else {
      setModalOpen(true)
    }
  }, [mode])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validate file size if restriction is set
      if (restrictions?.maxFileSize && file.size > restrictions.maxFileSize) {
        const maxSizeMB = (restrictions.maxFileSize / (1024 * 1024)).toFixed(1)
        const error = new Error(`File size exceeds ${maxSizeMB}MB limit`)
        onError?.(error)
        // Reset input
        event.target.value = ''
        return
      }

      setIsUploading(true)

      // Listen for completion to reset uploading state
      const handleComplete = () => {
        setIsUploading(false)
        uppy.off('complete', handleComplete)
        uppy.off('error', handleError)
        // Clear files from Uppy for next upload
        uppy.cancelAll()
      }

      const handleError = (error: Error) => {
        setIsUploading(false)
        uppy.off('complete', handleComplete)
        uppy.off('error', handleError)
        uppy.cancelAll()
        onError?.(error)
      }

      uppy.on('complete', handleComplete)
      uppy.on('error', handleError)

      // Add file to Uppy and trigger upload
      try {
        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
        })
        // Manually trigger upload since autoProceed is not enabled
        uppy.upload()
      } catch (err) {
        setIsUploading(false)
        uppy.off('complete', handleComplete)
        uppy.off('error', handleError)
        onError?.(err as Error)
      }

      // Reset input so the same file can be selected again
      event.target.value = ''
    },
    [uppy, restrictions?.maxFileSize, onError]
  )

  return (
    <>
      <Trigger
        icon="upload"
        text={uploadText}
        onClick={handleTriggerClick}
        disabled={disabled || isUploading}
        isBusy={isUploading}
      />
      {mode === 'modal' ? (
        <DashboardModal
          uppy={uppy}
          proudlyDisplayPoweredByUppy={false}
          open={modalOpen}
          onRequestClose={onRequestClose}
          closeModalOnClickOutside={false}
        />
      ) : (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      )}
    </>
  )
}
